/**
 * Lab-admin (LAB ADMIN phase B+C) data layer — the ONLY place this feature talks to the server. Every
 * path + capability below is code-verified against server/src/modules/lab-admin/{routes,controller,
 * validators}.js. Server data lives only in TanStack Query; mutations invalidate the affected
 * lab-scoped keys. A grant/membership write also refreshes the live capability set so the deny-wins
 * cockpit re-gates itself immediately (the admin can deny their own surface and instantly lose it).
 *
 * Endpoints consumed (all under /api/v1, all authed + capability-keyed; no public surface):
 *   GET    /lab-admin/profil                      lab_admin.profil.read
 *   PATCH  /lab-admin/profil                      lab_admin.profil.manage
 *   GET    /lab-admin/staff                       iam.utilisateur.manage
 *   POST   /lab-admin/staff                       iam.utilisateur.manage
 *   POST   /lab-admin/staff/membership            iam.utilisateur.manage
 *   POST   /lab-admin/staff/grants                iam.utilisateur.manage
 *   DELETE /lab-admin/staff/grants/:grantId       iam.utilisateur.manage
 *   POST   /lab-admin/staff/:id/statut            iam.utilisateur.manage
 *   GET    /lab-admin/journal                     audit.journal.read_all
 *   GET    /lab-admin/pricing                     catalogue.parametre.price
 *   PUT    /lab-admin/pricing                     catalogue.parametre.price
 *   GET    /lab-admin/abonnement                  lab_admin.abonnement.read
 *   POST   /lab-admin/abonnement/demande          lab_admin.abonnement.request
 *   GET    /lab-admin/sync-health                 lab_admin.abonnement.read
 */
import { api, qk, useApiMutation, useApiQuery } from "@/api";
import { useAuthStore } from "@/auth";
import type {
  AssignPriceResult,
  ChangeCycle,
  ChangeRequestResult,
  ChangeRequestType,
  GrantEffet,
  JournalFilters,
  JournalPage,
  LabProfile,
  PricingRow,
  ProfilePatch,
  StaffMember,
  Subscription,
  SyncHealth,
} from "./types";

/** Lab id for query scoping — from the live auth session (multi-tenant cache isolation). */
export function useLabId(): string | null {
  return useAuthStore((s) => s.user?.laboratoireId ?? s.lab?.id ?? null);
}

/** Lab-scoped query-key sub-factory for the lab-admin cockpit. */
export const adminKeys = {
  profile: (labId: string | null) => qk.scoped(labId, "lab-admin", "profile"),
  staff: (labId: string | null) => qk.scoped(labId, "lab-admin", "staff"),
  journal: (labId: string | null, filters: JournalFilters) =>
    qk.scoped(labId, "lab-admin", "journal", filters),
  pricing: (labId: string | null) => qk.scoped(labId, "lab-admin", "pricing"),
  subscription: (labId: string | null) => qk.scoped(labId, "lab-admin", "subscription"),
  syncHealth: (labId: string | null) => qk.scoped(labId, "lab-admin", "sync-health"),
};

/**
 * Re-pull the live effective capability set after a membership/grant change. A deny-wins write can
 * remove (or restore) a capability the very admin who made it depends on — so we refresh `can()`
 * immediately rather than waiting for the next page load (live per-request posture, never frozen).
 */
function useRefreshCaps(): () => Promise<void> {
  return useAuthStore((s) => s.refreshCapabilities);
}

// ── 1. Lab profile ───────────────────────────────────────────────────────────────
export function useProfile(labId: string | null, enabled: boolean) {
  return useApiQuery<LabProfile>(adminKeys.profile(labId), "/lab-admin/profil", {
    enabled,
    staleTime: 30_000,
  });
}

/** PATCH the editable profile fields (full CRUD by correction, L-1). Refreshes the profile view. */
export function useUpdateProfile(labId: string | null) {
  return useApiMutation<LabProfile, ProfilePatch>(
    (patch) => api.patch<LabProfile>("/lab-admin/profil", patch),
    { invalidate: [adminKeys.profile(labId)] },
  );
}

// ── 2. Staff & permissions ─────────────────────────────────────────────────────────
export function useStaff(labId: string | null, enabled: boolean) {
  return useApiQuery<StaffMember[]>(adminKeys.staff(labId), "/lab-admin/staff", {
    enabled,
    staleTime: 15_000,
  });
}

export interface CreateStaffVars {
  nom: string;
  prenom: string;
  email: string;
  password: string;
  presets?: string[];
  est_proprietaire?: boolean;
}

/** POST /lab-admin/staff — create a staff user (+ optional first membership). Refreshes the roster. */
export function useCreateStaff(labId: string | null) {
  return useApiMutation<unknown, CreateStaffVars>(
    (vars) => api.post("/lab-admin/staff", vars),
    { invalidate: [adminKeys.staff(labId)] },
  );
}

export interface SetMembershipVars {
  utilisateur_id: string | number;
  presets: string[];
  est_proprietaire?: boolean;
}

/** POST /lab-admin/staff/membership — replace a user's preset bundle + ownership. */
export function useSetMembership(labId: string | null) {
  const refreshCaps = useRefreshCaps();
  return useApiMutation<unknown, SetMembershipVars>(
    (vars) => api.post("/lab-admin/staff/membership", vars),
    { invalidate: [adminKeys.staff(labId)], onSuccess: () => refreshCaps() },
  );
}

export interface AddGrantVars {
  utilisateur_id: string | number;
  laboratoire_staff_id: string | number;
  capability_cle: string;
  effet: GrantEffet;
  motif?: string;
}

/** POST /lab-admin/staff/grants — add a grant/deny delta (deny-wins). Refreshes roster + live caps. */
export function useAddGrant(labId: string | null) {
  const refreshCaps = useRefreshCaps();
  return useApiMutation<unknown, AddGrantVars>(
    (vars) => api.post("/lab-admin/staff/grants", vars),
    { invalidate: [adminKeys.staff(labId)], onSuccess: () => refreshCaps() },
  );
}

/** NOVA-031 — the staff-lifecycle action set (the owner-gated suspend/revoke/archive trio). */
export type StaffLifecycleAction = "suspend" | "revoke" | "archive";

export interface SetStaffStatutVars {
  utilisateur_id: string;
  action: StaffLifecycleAction;
}

/**
 * POST /lab-admin/staff/:id/statut — suspend / revoke / archive a staff member. The server flips both the
 * membership statut (→ cap-cache drop) and the identity statut (→ session-kill + login reject), so we
 * refresh the roster AND the caller's own live caps (an owner could suspend themselves and instantly lose
 * the surface). Owner-gated server-side by iam.utilisateur.manage.
 */
export function useSetStaffStatut(labId: string | null) {
  const refreshCaps = useRefreshCaps();
  return useApiMutation<unknown, SetStaffStatutVars>(
    ({ utilisateur_id, action }) => api.post(`/lab-admin/staff/${utilisateur_id}/statut`, { action }),
    { invalidate: [adminKeys.staff(labId)], onSuccess: () => refreshCaps() },
  );
}

export interface RemoveGrantVars {
  grantId: string;
  utilisateur_id: string | number;
}

/** DELETE /lab-admin/staff/grants/:grantId — close a grant/deny delta (soft-delete, L-1). */
export function useRemoveGrant(labId: string | null) {
  const refreshCaps = useRefreshCaps();
  return useApiMutation<unknown, RemoveGrantVars>(
    ({ grantId, utilisateur_id }) =>
      api.delete(`/lab-admin/staff/grants/${grantId}`, { utilisateur_id }),
    { invalidate: [adminKeys.staff(labId)], onSuccess: () => refreshCaps() },
  );
}

// ── 3. Journal d'audit ───────────────────────────────────────────────────────────
/** GET /lab-admin/journal — filtered + server-paginated. The filters are part of the query key. */
export function useJournal(labId: string | null, filters: JournalFilters, enabled: boolean) {
  // Strip empties so the cache key is stable and the server gets a clean query.
  const query: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== null && v !== "") query[k] = v as string | number;
  }
  return useApiQuery<JournalPage>(adminKeys.journal(labId, filters), "/lab-admin/journal", {
    query,
    enabled,
    staleTime: 10_000,
  });
}

// ── 4. Analysis-type pricing ───────────────────────────────────────────────────────
export function usePricing(labId: string | null, enabled: boolean) {
  return useApiQuery<PricingRow[]>(adminKeys.pricing(labId), "/lab-admin/pricing", {
    enabled,
    staleTime: 20_000,
  });
}

export interface AssignPriceVars {
  parametre_code: string;
  prix: number; // integer centimes
  motif?: string; // mandatory ONLY on a change of an existing overlay (the server enforces)
}

/** PUT /lab-admin/pricing — instantiate (first price) or ledger-change a price. Refreshes the list. */
export function useAssignPrice(labId: string | null) {
  return useApiMutation<AssignPriceResult, AssignPriceVars>(
    (vars) => api.put<AssignPriceResult>("/lab-admin/pricing", vars),
    { invalidate: [adminKeys.pricing(labId)] },
  );
}

// ── 5. Own-subscription ─────────────────────────────────────────────────────────────
export function useSubscription(labId: string | null, enabled: boolean) {
  return useApiQuery<Subscription>(adminKeys.subscription(labId), "/lab-admin/abonnement", {
    enabled,
    staleTime: 30_000,
  });
}

/**
 * GET /lab-admin/sync-health — the master-data freshness (etat/derniereSynchroAt/graceExpireAt/
 * versionImportCourante). Same read cap as the subscription view. The cockpit tile + the perime banner
 * read this. `etat=perime` means the master-data channel is stale past grace (cloud caps suspend, the
 * operational core still runs) — surfaced, never silent. NOVA-063.
 */
export function useSyncHealth(labId: string | null, enabled: boolean) {
  return useApiQuery<SyncHealth>(adminKeys.syncHealth(labId), "/lab-admin/sync-health", {
    enabled,
    staleTime: 30_000,
  });
}

export interface ChangeRequestVars {
  type: ChangeRequestType;
  plan?: string; // required for upgrade/downgrade (the server enforces)
  cycle?: ChangeCycle;
  motif?: string;
}

/**
 * POST /lab-admin/abonnement/demande — enqueue an OUTBOUND change-request (best-effort, idempotent).
 * The channel is config-gated server-side: until the central SyncDomaine enum lands the server returns
 * a typed NOT_IMPLEMENTED (501) — the caller surfaces that as a "channel not yet enabled" notice, NOT
 * a fake success. No local subscription state is mutated; the lab cannot self-grant an entitlement.
 */
export function useRequestChange(labId: string | null) {
  return useApiMutation<ChangeRequestResult, ChangeRequestVars>(
    (vars) => api.post<ChangeRequestResult>("/lab-admin/abonnement/demande", vars),
    { invalidate: [adminKeys.subscription(labId)] },
  );
}
